import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/SEO.tsx';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 8px 24px rgba(255, 255, 255, 0.1)',
  },
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const StyledIcon = styled(Box)({
  fontSize: '3rem',
  marginBottom: '1rem',
  background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});

const services = [
  {
    icon: CodeIcon,
    title: 'Java Development',
    description: 'Development of robust applications with Java, Spring Boot, and the latest technologies.',
  },
  {
    icon: StorageIcon,
    title: 'Database',
    description: 'Design and optimization of relational and NoSQL databases.',
  },
  {
    icon: CloudIcon,
    title: 'Cloud Computing',
    description: 'Deployment and management of applications on AWS, Azure, and Google Cloud Platform.',
  },
  {
    icon: SecurityIcon,
    title: 'Security',
    description: 'Implementation of best security practices and data protection.',
  },
  {
    icon: SpeedIcon,
    title: 'Performance',
    description: 'Performance optimization and application scaling.',
  },
  {
    icon: BuildIcon,
    title: 'Maintenance',
    description: 'Technical support and ongoing maintenance of existing applications.',
  },
];

const Services = () => {
  useEffect(() => {
    document.title = 'Alexandre Mami | Services';
  }, []);

  return (
    <>
      <SEO
        title="Services"
        description="Découvrez les services de développement Full Stack d'Alexandre Mami : Java, Spring Framework, React, Cloud Computing, Sécurité et Performance."
        keywords="services développement, java, spring, react, cloud, sécurité, performance, alexandre mami"
        ogImage="https://mamialex.ru/images/services-preview.jpg"
        ogType="service"
      />
      <Box
        sx={{
          minHeight: '100%',
          py: 8,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        }}
      >
        <Container maxWidth="lg">
            <Typography
            variant="h2"
            gutterBottom
            sx={{
              textAlign: 'center',
              mb: 6,
              background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
            }}
          >
            My Services
          </Typography>

          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              mb: 8,
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            I offer a complete range of development services to meet your needs
            for professional software solutions.
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StyledCard>
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 4,
                    }}
                  >
                    <StyledIcon>
                      <service.icon sx={{ fontSize: '3rem' }} />
                    </StyledIcon>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.7,
                      }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              mt: 8,
              p: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Need a custom service?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Don't hesitate to contact me to discuss your specific needs.
              I'm here to help you realize your projects.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Services; 